import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Stack, Chip, Avatar,
  Breadcrumbs, Link, Alert, CircularProgress, Divider, Paper, Container,
  List, ListItem, ListItemIcon, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  ArrowBack as BackIcon, Edit as EditIcon, Person as PersonIcon,
  Business as BusinessIcon, Email as EmailIcon, Phone as PhoneIcon,
  LocationOn as LocationIcon, Block as BlockIcon, Devices as DevicesIcon,
  CalendarToday as CalendarIcon, Code as CodeIcon,
  CreditCard as SimIcon, Timeline as TimelineIcon, Info as InfoIcon,
  GpsFixed as GpsIcon,
  Subscriptions as SubIcon, Settings as ConfigIcon,
  PhoneAndroid as PhoneAndroidIcon, Apple as AppleIcon,
  RadioButtonChecked as ActiveIcon, RadioButtonUnchecked as InactiveIcon,
  MyLocation as SafeZoneIcon, CarCrash as AccidentIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import EditorConfiguracionUsuario from '../../components/admin/EditorConfiguracionUsuario';
import { formatDate, getDeviceDisplayName, getDeviceSubtitle } from '../../utils/helpers';

interface DeviceCardProps { device: any; }

const DeviceCard: React.FC<DeviceCardProps> = ({ device }) => {
  const getColor = (s: string) => {
    switch (s) { case 'active': case 'online': return 'success'; case 'inactive': case 'offline': return 'default'; default: return 'warning'; }
  };
  const exp = device.expirationTime ? new Date(device.expirationTime) : null;
  const now = new Date();
  const days = exp ? Math.ceil((exp.getTime() - now.getTime()) / 86400000) : null;
  const isExpired = days !== null && days < 0;
  const isSoon = days !== null && days >= 0 && days <= 30;

  return (
    <Card sx={{ height: '100%', '&:hover': { boxShadow: 4 } }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600}>{getDeviceDisplayName(device)}</Typography>
          <Chip label={device.status} color={getColor(device.status)} size="small" />
        </Box>
        <Typography variant="caption" color="text.secondary">{getDeviceSubtitle(device) || `IMEI: ${device.imei}`}</Typography>
        <Divider sx={{ my: 1.5 }} />
        <Stack spacing={0.5}>
          {device.simCardId && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SimIcon fontSize="small" color="action" />
              <Typography variant="body2">SIM: {device.simCardId.iccid?.slice(-6)}</Typography>
              {device.simCardId.phoneNumber && <Typography variant="caption">({device.simCardId.phoneNumber})</Typography>}
            </Box>
          )}
          {exp && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon fontSize="small" color={isExpired ? 'error' : isSoon ? 'warning' : 'action'} />
              <Typography variant="body2" color={isExpired ? 'error.main' : isSoon ? 'warning.main' : undefined}>
                {isExpired ? `Expirado hace ${Math.abs(days!)} días` : `${days} días restantes`}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const ClientDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();

  const { data: clientData, loading, error, execute: fetchClient } = useApi(
    () => clientId ? apiService.getClient(clientId) : Promise.reject('No client ID'),
    { immediate: false }
  );

  useEffect(() => { if (clientId) fetchClient(); }, [clientId, fetchClient]);

  const { user } = useAuth();
  // Lo último guardado desde esta ficha. Recargar el cliente entero pone la página en blanco
  // con el spinner y cierra el editor, así que se usa la respuesta del guardado.
  const [configGuardada, setConfigGuardada] = useState<any>(undefined);
  useEffect(() => { setConfigGuardada(undefined); }, [clientId]);
  const [zonaSeguraDialog, setZonaSeguraDialog] = useState(false);
  const [zonaSeguraGuardando, setZonaSeguraGuardando] = useState(false);
  const [zonaSeguraError, setZonaSeguraError] = useState<string | null>(null);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 400, alignItems: 'center' }}><CircularProgress /></Box>;
  if (error) return <Container maxWidth="lg" sx={{ mt: 4 }}><Alert severity="error">Error: {error}<Button onClick={() => fetchClient()} sx={{ ml: 2 }}>Reintentar</Button></Alert></Container>;

  const client = (clientData as any)?.client;
  const devices = (clientData as any)?.devices || [];
  const stats = (clientData as any)?.stats || {};
  const configuration = configGuardada !== undefined ? configGuardada : (clientData as any)?.configuration;
  const subscription = (clientData as any)?.subscription;

  if (!client) return <Container maxWidth="lg" sx={{ mt: 4 }}><Alert severity="warning">Cliente no encontrado</Alert></Container>;

  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  // Zona Segura se concede a petición, tenga o no suscripción: el permiso vive en la
  // configuración del usuario y la app lo lee aunque no haya plan. Antes solo se podía dar
  // desde el detalle de una suscripción, así que a quien no tenía una había que inventársela.
  const zonaSeguraConcedida = configuration?.safeZone?.enabled_independent === true;
  const cambiarZonaSegura = async () => {
    if (!client?.traccarUserId) return;
    setZonaSeguraGuardando(true);
    setZonaSeguraError(null);
    try {
      // Ruta con punto a propósito: mandar `safeZone` entero reemplazaría el radio y el
      // auto-apagado que el cliente ya configuró.
      const r: any = await apiService.updateSubscriptionConfig(String(client.traccarUserId), {
        'safeZone.enabled_independent': !zonaSeguraConcedida,
      });
      setConfigGuardada(r?.data ?? r);
      setZonaSeguraDialog(false);
    } catch (e: any) {
      setZonaSeguraError(e.response?.data?.error || e.message);
    } finally {
      setZonaSeguraGuardando(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component="button" variant="body2" onClick={() => navigate('/clients')} sx={{ textDecoration: 'none' }}>Clientes</Link>
          <Typography variant="body2" color="text.primary">{client.name}</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Button startIcon={<BackIcon />} onClick={() => navigate('/clients')} variant="text">Volver</Button>
          <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: 24 }}>{getInitials(client.name)}</Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" fontWeight={600}>{client.name}</Typography>
              <Chip label={client.status === 'active' ? 'Activo' : client.status} color={client.status === 'active' ? 'success' : 'error'} size="small" />
              <Chip label={client.clientType === 'business' ? 'Empresa' : 'Individual'} variant="outlined" size="small"
                icon={client.clientType === 'business' ? <BusinessIcon /> : <PersonIcon />} />
            </Box>
            <Typography variant="body2" color="text.secondary">{client.code} | Traccar ID: {client.traccarUserId || 'N/A'}</Typography>
          </Box>
          <Button variant="contained" startIcon={<EditIcon />} onClick={() => navigate(`/clients/${clientId}/edit`)}>Editar</Button>
        </Box>
      </Box>

      {/* Stats Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Dispositivos', value: stats.totalDevices || 0, color: 'primary.main' },
          { label: 'Activos', value: stats.activeDevices || 0, color: 'success.main' },
          { label: 'Expirados', value: stats.expiredDevices || 0, color: 'error.main' },
          { label: 'Renovaciones', value: stats.completedRenewals || 0, color: 'info.main' },
        ].map((s, i) => (
          <Grid size={{ xs: 6, sm: 3 }} key={i}>
            <Card sx={{ textAlign: 'center' }}>
              <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="h4" fontWeight={600} color={s.color}>{s.value}</Typography>
                <Typography variant="body2" color="text.secondary">{s.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Stack spacing={3}>
            {/* Contact Info */}
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon color="primary" /> Información de Contacto
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <List disablePadding>
                      <ListItem disablePadding sx={{ mb: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}><EmailIcon color="action" /></ListItemIcon>
                        <ListItemText primary="Email" secondary={client.email}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }} />
                      </ListItem>
                      <ListItem disablePadding sx={{ mb: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}><PhoneIcon color="action" /></ListItemIcon>
                        <ListItemText primary="Teléfono" secondary={client.phoneNumber || 'N/A'}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }} />
                      </ListItem>
                      {client.address && (client.address.street || client.address.city) && (
                        <ListItem disablePadding>
                          <ListItemIcon sx={{ minWidth: 36 }}><LocationIcon color="action" /></ListItemIcon>
                          <ListItemText primary="Dirección"
                            secondary={[client.address.street, client.address.city, client.address.country].filter(Boolean).join(', ')}
                            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }} />
                        </ListItem>
                      )}
                    </List>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <List disablePadding>
                      <ListItem disablePadding sx={{ mb: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}><CodeIcon color="action" /></ListItemIcon>
                        <ListItemText primary="Código" secondary={client.code}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                          secondaryTypographyProps={{ fontWeight: 600 }} />
                      </ListItem>
                      <ListItem disablePadding sx={{ mb: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}><CalendarIcon color="action" /></ListItemIcon>
                        <ListItemText primary="Registro" secondary={formatDate(client.createdAt)}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }} />
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemIcon sx={{ minWidth: 36 }}><GpsIcon color="action" /></ListItemIcon>
                        <ListItemText primary="Traccar User ID" secondary={client.traccarUserId || 'No asignado'}
                          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                          secondaryTypographyProps={{ fontFamily: 'monospace' }} />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Subscription */}
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SubIcon color="primary" /> Suscripción
                </Typography>
                {subscription ? (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Plan</Typography>
                      <Box>
                        <Chip label={subscription.plan?.toUpperCase()} color="primary" size="small" />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Estado</Typography>
                      <Box>
                        <Chip
                          label={subscription.status}
                          color={subscription.status === 'active' ? 'success' : subscription.status === 'expired' ? 'error' : 'warning'}
                          size="small"
                          icon={subscription.status === 'active' ? <ActiveIcon /> : <InactiveIcon />}
                        />
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Expira</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {subscription.expiresAt ? new Date(subscription.expiresAt).toLocaleDateString('es-DO') : 'N/A'}
                      </Typography>
                      {subscription.expiresAt && new Date(subscription.expiresAt) < new Date() && (
                        <Chip label="EXPIRADO" color="error" size="small" sx={{ mt: 0.5 }} />
                      )}
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Plataforma</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {subscription.platform === 'ios' ? <AppleIcon fontSize="small" /> : <PhoneAndroidIcon fontSize="small" />}
                        <Typography variant="body2">{subscription.platform || 'N/A'}</Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Auto-renovación</Typography>
                      <Box><Chip label={subscription.autoRenew ? 'Sí' : 'No'} color={subscription.autoRenew ? 'success' : 'default'} size="small" /></Box>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Product ID</Typography>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">{subscription.productId || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">Último Evento</Typography>
                      <Typography variant="body2">{subscription.lastRevenueCatEvent || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                      <Typography variant="caption" color="text.secondary">RevenueCat ID</Typography>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.75rem">{subscription.revenueCatCustomerId || 'N/A'}</Typography>
                    </Grid>
                  </Grid>
                ) : (
                  <Alert severity="info">Este cliente no tiene suscripción registrada (usuario free o sin app)</Alert>
                )}
              </CardContent>
            </Card>

            {/* Configuration */}
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ConfigIcon color="primary" /> Configuración del Usuario
                </Typography>
                {client.traccarUserId ? (
                  <EditorConfiguracionUsuario
                    userId={String(client.traccarUserId)}
                    configuracion={configuration || null}
                    puedeEditar={user?.role === 'admin'}
                    onGuardado={setConfigGuardada}
                  />
                ) : (
                  <Alert severity="info">Este cliente no tiene usuario de Traccar, así que no tiene configuración de la app.</Alert>
                )}
              </CardContent>
            </Card>

            {/* Devices */}
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DevicesIcon color="primary" /> Dispositivos ({devices.length})
                </Typography>
                {devices.length === 0 ? (
                  <Alert severity="info">Sin dispositivos asignados</Alert>
                ) : (
                  <Grid container spacing={2}>
                    {devices.map((d: any) => (
                      <Grid key={d._id} size={{ xs: 12, md: 6, lg: 4 }}>
                        <DeviceCard device={d} />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </CardContent>
            </Card>

            {/* Notes */}
            {client.notes && (
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <InfoIcon color="primary" /> Notas
                  </Typography>
                  <Typography variant="body1">{client.notes}</Typography>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>

        {/* Right Column */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Stack spacing={3}>
            {/* Quick Actions */}
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Acciones Rápidas</Typography>
                <Stack spacing={1.5}>
                  <Button fullWidth variant="contained" startIcon={<EditIcon />}
                    onClick={() => navigate(`/clients/${clientId}/edit`)}>Editar Cliente</Button>
                  <Button fullWidth variant="outlined" color={client.status === 'active' ? 'error' : 'success'}
                    startIcon={<BlockIcon />}>
                    {client.status === 'active' ? 'Desactivar' : 'Activar'} Cliente
                  </Button>
                  {user?.role === 'admin' && (
                    <Button fullWidth variant={zonaSeguraConcedida ? 'outlined' : 'contained'} color="warning"
                      startIcon={<SafeZoneIcon />} disabled={!client.traccarUserId}
                      onClick={() => { setZonaSeguraError(null); setZonaSeguraDialog(true); }}>
                      {zonaSeguraConcedida ? 'Retirar Zona Segura' : 'Conceder Zona Segura'}
                    </Button>
                  )}
                  {user?.role === 'admin' && !client.traccarUserId && (
                    <Typography variant="caption" color="text.secondary">
                      Para conceder Zona Segura el cliente necesita usuario de Traccar.
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Additional Contacts */}
            {client.contacts && client.contacts.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Contactos</Typography>
                  <Stack spacing={1.5}>
                    {client.contacts.map((c: any, i: number) => (
                      <Paper key={i} variant="outlined" sx={{ p: 1.5 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {c.name} {c.isPrimary && <Chip label="Principal" size="small" sx={{ ml: 0.5, height: 18 }} />}
                        </Typography>
                        {c.phone && <Typography variant="body2">{c.phone}</Typography>}
                        {c.email && <Typography variant="caption" color="text.secondary">{c.email}</Typography>}
                      </Paper>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Timeline placeholder */}
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TimelineIcon color="primary" /> Actividad Reciente
                </Typography>
                <Typography variant="body2" color="text.secondary">Próximamente</Typography>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
      <Dialog open={zonaSeguraDialog} onClose={() => !zonaSeguraGuardando && setZonaSeguraDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{zonaSeguraConcedida ? 'Retirar Zona Segura' : 'Conceder Zona Segura'}</DialogTitle>
        <DialogContent>
          {zonaSeguraConcedida ? (
            <Typography variant="body2">
              <strong>{client.name}</strong> dejará de tener acceso a Zona Segura, salvo que su plan la incluya.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                <strong>{client.name}</strong> podrá usar Zona Segura desde la app aunque no tenga plan ni suscripción.
              </Typography>
              <Alert severity="warning">
                Zona Segura puede apagar el vehículo sola. Concédela solo a quien sepa usarla.
              </Alert>
            </>
          )}
          {zonaSeguraError && <Alert severity="error" sx={{ mt: 1.5 }}>{zonaSeguraError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZonaSeguraDialog(false)} disabled={zonaSeguraGuardando}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={cambiarZonaSegura} disabled={zonaSeguraGuardando}>
            {zonaSeguraGuardando ? <CircularProgress size={20} /> : zonaSeguraConcedida ? 'Retirar' : 'Conceder'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClientDetailPage;
